/**
 * Verortung eines Eintrags auf den Projektplaenen (docs/planverortung-vertrag.md, P3).
 *
 * Betreiber 28.09.2026: ein Mangel kann an MEHREREN Stellen verortet sein,
 * auch auf mehreren Plaenen. Der Dialog bleibt deshalb offen — jeder Tipp auf
 * den Plan setzt eine weitere Nadel, ein Tipp auf eine eigene Nadel entfernt
 * sie, Planwechsel ueber die Reiter ersetzt nichts. Heraus geht es ueber den
 * grossen „Fertig“-Knopf, das X oder Esc; jede Aenderung gilt sofort (der
 * Aufrufer schreibt sie ueber services/planPins.ts in den Eintrag).
 *
 * Am Handy Vollbild, ab `md` ein Modal. Zoom ueber die BREITE der Planflaeche
 * im Scrollbereich — kein `transform: scale`, sonst stimmen Scrollweg und
 * Tippkoordinaten nicht mehr.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Map as MapIcon, MapPin, Plus, Upload, X, ZoomIn, ZoomOut } from 'lucide-react';
import type { ProjectPlan } from '../../types';
import type { PlanPin } from '../../services/planPins';
import type { LivePunkt } from '../../services/verortung/liveStandort';
import type { LiveStandort } from '../verortung/useLiveStandort';
import { LiveStandortLeiste, LiveStandortMarker } from '../verortung/LiveStandort';
import { nadelNeigungen } from '../../services/planNadelNeigung';
import { PlanNadel, nadelAbstandPx } from './PlanNadel';
import { PlanAnlegen } from './PlanAnlegen';

export interface PlanVerortungDialogProps {
    plaene: ProjectPlan[];                 // schon nach Abschnitt gefiltert
    startPlanId?: string | null;
    pins: PlanPin[];                       // pinsVon(eintrag)
    nummer?: string | number | null;       // Text im Nadelkopf
    onPinSetzen: (pin: PlanPin) => void;
    onPinEntfernen: (index: number) => void;
    onFertig: () => void;
    planAnlegen?: { projectId: string; sectionId?: string; onAngelegt: (plan: ProjectPlan) => void };
    live?: LiveStandort;                   // useLiveStandort
    onLiveUebernehmen?: (planId: string, punkt: LivePunkt) => void;
    onPlanGetippt?: (planId: string) => void;   // live.planBestaetigen
    language?: 'de' | 'en';
    /**
     * An `document.body` haengen (Standard). Im Seiteninhalt liegt der Dialog
     * sonst in einem fremden Stapelkontext — Sidebar und Berichtskopf lagen
     * darueber (Betreiber-Screenshot 28.09.). Der Handy-Assistent setzt false:
     * dort gehoert der Dialog in seine Vollbildflaeche (Android-Zurueck-Taste).
     */
    imPortal?: boolean;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_SCHRITT = 0.5;
/** Luft um den Plan bei Zoom 1 (px), damit Nadeln am Rand nicht anstossen. */
const RAND_PX = 16;
/** Ohne Messung (erster Render, Server-Markup): angenommene Planbreite (px) bei Zoom 1. */
const RUECKFALL_BREITE_PX = 1000;

const TEXTE = {
    de: {
        titel: 'Verortung auf Plan',
        planPlus: 'Plan',
        verkleinern: 'Verkleinern',
        vergroessern: 'Vergrößern',
        schliessen: 'Schließen',
        keineStelle: 'Noch keine Stelle markiert',
        stellen: (n: number) => (n === 1 ? '1 Stelle markiert' : `${n} Stellen markiert`),
        aufPlaenen: (m: number) => ` auf ${m} Plänen`,
        hinweisLeer: 'Auf den Plan tippen = Stelle markieren',
        hinweis: 'Tippen = weitere Stelle · Nadel antippen = entfernen',
        nadelTitel: 'Antippen zum Entfernen',
        fertig: 'Fertig',
        keinPlan: 'Noch kein Plan im Projekt',
        hochladen: 'Plan hochladen',
        laedt: 'Wird hochgeladen …',
        hinweisReiter: 'Pläne im Reiter ‚Pläne‘ hochladen',
    },
    en: {
        titel: 'Location on plan',
        planPlus: 'Plan',
        verkleinern: 'Zoom out',
        vergroessern: 'Zoom in',
        schliessen: 'Close',
        keineStelle: 'No location marked yet',
        stellen: (n: number) => (n === 1 ? '1 location marked' : `${n} locations marked`),
        aufPlaenen: (m: number) => ` on ${m} plans`,
        hinweisLeer: 'Tap the plan = mark location',
        hinweis: 'Tap = add location · Tap pin = remove',
        nadelTitel: 'Tap to remove',
        fertig: 'Done',
        keinPlan: 'No plan in this project yet',
        hochladen: 'Upload plan',
        laedt: 'Uploading …',
        hinweisReiter: "Upload plans in the 'Plans' tab",
    },
} as const;

const prozent = (wert: number): number => Math.min(100, Math.max(0, wert));

/** Startplan: gewuenschter Plan, sonst der Plan der ersten Nadel, sonst der erste Plan. */
const startPlanWaehlen = (plaene: readonly ProjectPlan[], startPlanId: string | null | undefined, pins: readonly PlanPin[]): string | null => {
    if (startPlanId && plaene.some(p => p.id === startPlanId)) return startPlanId;
    const ersteNadel = pins.find(pin => plaene.some(p => p.id === pin.planId));
    if (ersteNadel) return ersteNadel.planId;
    return plaene[0]?.id ?? null;
};

export function PlanVerortungDialog({
    plaene, startPlanId, pins, nummer, onPinSetzen, onPinEntfernen, onFertig,
    planAnlegen, live, onLiveUebernehmen, onPlanGetippt, language = 'de', imPortal = true,
}: PlanVerortungDialogProps): JSX.Element {
    const t = TEXTE[language === 'en' ? 'en' : 'de'];

    // Gewaehlter Reiter. Ein gerade angelegter Plan wird hier sofort gemerkt,
    // auch wenn er erst beim naechsten Render in `plaene` steht — bis dahin
    // gilt der Rueckfall (erster Plan), danach der neue.
    const [gewaehlt, setGewaehlt] = useState<string | null>(() => startPlanWaehlen(plaene, startPlanId, pins));
    const aktiverPlan = plaene.find(p => p.id === gewaehlt) ?? plaene[0] ?? null;
    const aktivId = aktiverPlan?.id ?? null;

    const [zoom, setZoom] = useState(ZOOM_MIN);
    // Seitenverhaeltnis je Plan (aus dem geladenen Bild) fuer „ganzer Plan sichtbar“ bei Zoom 1.
    const [verhaeltnis, setVerhaeltnis] = useState<Record<string, number>>({});
    // Groesse der Planflaeche (Scrollbereich), gemessen per ResizeObserver.
    const [flaeche, setFlaeche] = useState<{ b: number; h: number } | null>(null);
    const flaecheRef = useRef<HTMLDivElement>(null);
    // Mittelpunkt vor dem Zoomen (Anteil der Scrollbreite/-hoehe), damit der Ausschnitt stehen bleibt.
    const zoomAnker = useRef<{ fx: number; fy: number } | null>(null);

    // Esc = Fertig. Ein Dialog darueber (Zuschnitt beim Plan-Anlegen), der Esc
    // selbst behandelt und `preventDefault` ruft, hat Vorrang.
    useEffect(() => {
        const taste = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return;
            // Offener Plan-Zuschnitt (PlanAnlegen, Portal): Esc bricht nur ihn ab — schloesse der
            // Dialog hier, verschwaende der Zuschneider samt Arbeit mit ihm.
            if (document.querySelector('[data-plan-zuschnitt]')) return;
            onFertig();
        };
        window.addEventListener('keydown', taste);
        return () => window.removeEventListener('keydown', taste);
    }, [onFertig]);

    const hatPlan = aktiverPlan !== null;
    useEffect(() => {
        const el = flaecheRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        // Der Beobachter meldet sich beim Anmelden einmal von selbst (Erstmessung).
        const beobachter = new ResizeObserver(() => setFlaeche({ b: el.clientWidth, h: el.clientHeight }));
        beobachter.observe(el);
        return () => beobachter.disconnect();
    }, [hatPlan]);

    // Nach dem Zoomen den vorherigen Mittelpunkt wieder in die Mitte scrollen.
    useLayoutEffect(() => {
        const el = flaecheRef.current;
        const anker = zoomAnker.current;
        if (!el || !anker) return;
        zoomAnker.current = null;
        el.scrollLeft = anker.fx * el.scrollWidth - el.clientWidth / 2;
        el.scrollTop = anker.fy * el.scrollHeight - el.clientHeight / 2;
    }, [zoom]);

    const zoomAendern = (richtung: 1 | -1) => {
        const el = flaecheRef.current;
        if (el && el.scrollWidth > 0 && el.scrollHeight > 0) {
            zoomAnker.current = {
                fx: (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth,
                fy: (el.scrollTop + el.clientHeight / 2) / el.scrollHeight,
            };
        }
        setZoom(z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + richtung * ZOOM_SCHRITT)));
    };

    const planWechseln = (planId: string) => {
        if (planId === aktivId) return;
        setGewaehlt(planId);
        setZoom(ZOOM_MIN);
    };

    const angelegt = (plan: ProjectPlan) => {
        setGewaehlt(plan.id);
        setZoom(ZOOM_MIN);
        planAnlegen?.onAngelegt(plan);
    };

    const tippen = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!aktiverPlan) return;
        const r = e.currentTarget.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) return;
        const x = prozent(((e.clientX - r.left) / r.width) * 100);
        const y = prozent(((e.clientY - r.top) / r.height) * 100);
        onPinSetzen({ x, y, planId: aktiverPlan.id });
        // Tipp auf den Plan eines Geschosses = Korrektur „hier bin ich“ (neuer Nullpunkt).
        onPlanGetippt?.(aktiverPlan.id);
    };

    // Eigene Nadeln des aktiven Plans mit ihrem GESAMTindex (zum Entfernen).
    const eigeneNadeln = pins
        .map((pin, index) => ({ ...pin, index }))
        .filter(pin => pin.planId === aktivId);
    const anzahlJePlan = (planId: string) => pins.filter(pin => pin.planId === planId).length;
    const planAnzahl = new Set(pins.map(pin => pin.planId)).size;

    const livePunkt = live && live.aktiv && aktivId ? live.punktFuer(aktivId) : null;
    const livePlanWechsel = live && live.livePlanId && live.livePlanId !== aktivId && plaene.some(p => p.id === live.livePlanId)
        ? live.livePlanId
        : null;

    // Breite der Planflaeche: bei Zoom 1 passt der ganze Plan hinein (Breite UND
    // Hoehe), jeder Zoomschritt vergroessert die Breite. Ohne Messung (erster
    // Render, Server-Markup): Prozent der Flaeche.
    const seitenVerhaeltnis = aktivId ? verhaeltnis[aktivId] : undefined;
    const breitePx = flaeche && seitenVerhaeltnis && flaeche.b > RAND_PX && flaeche.h > RAND_PX
        ? Math.max(1, Math.round(Math.min(flaeche.b - RAND_PX, (flaeche.h - RAND_PX) * seitenVerhaeltnis) * zoom))
        : null;
    const breite = breitePx !== null ? `${breitePx}px` : `${zoom * 100}%`;

    // Nahe beieinander stehende Nadeln faechern auf (Betreiber 28.09.: „drehe
    // sie leicht weg“) — gerechnet in Bildschirm-PIXELN des gezeichneten Plans,
    // denn die Kugeln sind bei jedem Zoom gleich gross. Hoehe ueber das
    // Seitenverhaeltnis; vor dem Laden des Bilds A4 quer (Zuschnitt-Standard).
    const planBreitePx = breitePx ?? (flaeche && flaeche.b > 0 ? flaeche.b : RUECKFALL_BREITE_PX) * zoom;
    const planHoehePx = planBreitePx / (seitenVerhaeltnis && seitenVerhaeltnis > 0 ? seitenVerhaeltnis : Math.SQRT2);
    const neigungen = nadelNeigungen(
        eigeneNadeln.map(pin => ({ x: (pin.x / 100) * planBreitePx, y: (pin.y / 100) * planHoehePx })),
        nadelAbstandPx('normal'),
    );

    const stellenText = pins.length === 0
        ? t.keineStelle
        : `${t.stellen(pins.length)}${planAnzahl > 1 ? t.aufPlaenen(planAnzahl) : ''}`;

    const anlegenKnopf = (gross: boolean) => planAnlegen && (
        <PlanAnlegen
            projectId={planAnlegen.projectId}
            sectionId={planAnlegen.sectionId}
            anzahlVorhanden={plaene.length}
            onAngelegt={angelegt}
        >
            {(oeffnen, laedt) => (gross ? (
                <button
                    type="button"
                    onClick={oeffnen}
                    disabled={laedt}
                    className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-base font-bold text-white shadow-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60"
                >
                    {laedt ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                    {laedt ? t.laedt : t.hochladen}
                </button>
            ) : (
                <button
                    type="button"
                    onClick={oeffnen}
                    disabled={laedt}
                    title={t.hochladen}
                    className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-slate-500 px-3 py-1.5 text-sm font-semibold text-slate-200 hover:bg-slate-700 disabled:opacity-60"
                >
                    {laedt ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    {t.planPlus}
                </button>
            ))}
        </PlanAnlegen>
    );

    const inhalt = (
        <div
            className="fixed inset-0 z-[120] flex items-stretch justify-center bg-slate-900 animate-fade-in md:items-center md:bg-black/80 md:p-4 md:backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label={t.titel}
        >
            <div className="flex h-full w-full flex-col overflow-hidden bg-slate-900 text-white md:h-[92vh] md:max-w-[95vw] md:rounded-xl md:border md:border-slate-700 md:shadow-2xl">
                {/* Kopf: Titel, Zoom, Schliessen; darunter die Plan-Reiter */}
                <div className="shrink-0 border-b border-slate-700 bg-slate-800">
                    <div className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-3">
                        <h2 className="flex min-w-0 flex-1 items-center gap-2 text-base font-bold md:text-lg">
                            <MapPin className="h-5 w-5 shrink-0 text-red-400" aria-hidden="true" />
                            <span className="truncate">{t.titel}</span>
                        </h2>
                        {aktiverPlan && (
                            <div className="flex items-center rounded-lg bg-slate-700 p-1">
                                <button
                                    type="button"
                                    onClick={() => zoomAendern(-1)}
                                    disabled={zoom <= ZOOM_MIN}
                                    aria-label={t.verkleinern}
                                    title={t.verkleinern}
                                    className="rounded p-2 hover:bg-slate-600 disabled:opacity-40 md:p-1.5"
                                >
                                    <ZoomOut className="h-5 w-5" />
                                </button>
                                <span className="w-11 text-center text-xs font-semibold tabular-nums">{Math.round(zoom * 100)} %</span>
                                <button
                                    type="button"
                                    onClick={() => zoomAendern(1)}
                                    disabled={zoom >= ZOOM_MAX}
                                    aria-label={t.vergroessern}
                                    title={t.vergroessern}
                                    className="rounded p-2 hover:bg-slate-600 disabled:opacity-40 md:p-1.5"
                                >
                                    <ZoomIn className="h-5 w-5" />
                                </button>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={onFertig}
                            aria-label={t.schliessen}
                            title={t.schliessen}
                            className="rounded-lg border border-slate-600 p-2 hover:bg-slate-700"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    {plaene.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto px-3 pb-2 md:px-4" role="tablist">
                            {plaene.map(p => {
                                const aktiv = p.id === aktivId;
                                const anzahl = anzahlJePlan(p.id);
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={aktiv}
                                        onClick={() => planWechseln(p.id)}
                                        className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-semibold ${aktiv ? 'border-blue-400 bg-blue-600 text-white' : 'border-slate-600 bg-slate-700/60 text-slate-200 hover:bg-slate-700'}`}
                                    >
                                        <span className="max-w-[12rem] truncate">{p.name}</span>
                                        {anzahl > 0 && (
                                            <span className="min-w-[1.25rem] rounded-full bg-red-600 px-1.5 text-center text-[11px] font-bold leading-5 text-white">
                                                {anzahl}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                            {anlegenKnopf(false)}
                        </div>
                    )}
                </div>

                {live && aktiverPlan && (
                    <LiveStandortLeiste
                        live={live}
                        punktImPlan={livePunkt}
                        onUebernehmen={livePunkt && onLiveUebernehmen ? () => onLiveUebernehmen(aktiverPlan.id, livePunkt) : undefined}
                        onZumLivePlan={livePlanWechsel ? () => planWechseln(livePlanWechsel) : undefined}
                    />
                )}

                {aktiverPlan ? (
                    <div ref={flaecheRef} className="flex min-h-0 flex-1 overflow-auto bg-black">
                        <div className="m-auto shrink-0" style={{ width: breite }}>
                            <div className="relative isolate cursor-crosshair select-none" onClick={tippen}>
                                <img
                                    key={aktiverPlan.id}
                                    src={aktiverPlan.image}
                                    alt={aktiverPlan.name}
                                    draggable={false}
                                    className="block h-auto w-full"
                                    onLoad={(e) => {
                                        const img = e.currentTarget;
                                        if (!img.naturalWidth || !img.naturalHeight) return;
                                        const v = img.naturalWidth / img.naturalHeight;
                                        const id = aktiverPlan.id;
                                        setVerhaeltnis(prev => (prev[id] === v ? prev : { ...prev, [id]: v }));
                                    }}
                                />
                                {livePunkt && <LiveStandortMarker punkt={livePunkt} />}
                                {eigeneNadeln.map((pin, i) => (
                                    <PlanNadel
                                        key={pin.index}
                                        x={pin.x}
                                        y={pin.y}
                                        neigung={neigungen[i]}
                                        nummer={nummer}
                                        onClick={() => onPinEntfernen(pin.index)}
                                        entfernbar
                                        title={t.nadelTitel}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-auto p-6 text-center">
                        <div className="rounded-full bg-slate-800 p-5">
                            <MapIcon className="h-10 w-10 text-slate-400" aria-hidden="true" />
                        </div>
                        <p className="text-lg font-bold">{t.keinPlan}</p>
                        {planAnlegen ? anlegenKnopf(true) : <p className="text-sm text-slate-400">{t.hinweisReiter}</p>}
                    </div>
                )}

                {/* Fussleiste: Zaehler, Hinweis, grosser Fertig-Knopf */}
                <div className="flex shrink-0 items-center gap-3 border-t border-slate-700 bg-slate-800 px-4 pt-3 pb-safe">
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold md:text-base" aria-live="polite">{stellenText}</p>
                        {aktiverPlan && (
                            <p className="text-xs text-slate-400">{pins.length === 0 ? t.hinweisLeer : t.hinweis}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onFertig}
                        className="flex min-h-[48px] shrink-0 items-center gap-2 rounded-xl bg-green-700 px-6 text-base font-bold text-white shadow-lg hover:bg-green-800 active:bg-green-900"
                    >
                        <Check className="h-5 w-5" aria-hidden="true" />
                        {t.fertig}
                    </button>
                </div>
            </div>
        </div>
    );
    // Ohne DOM (Server-Rendern in den Tests) steht der Dialog an Ort und Stelle.
    return imPortal && typeof document !== 'undefined' ? <>{createPortal(inhalt, document.body)}</> : inhalt;
}
